import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  applyBranchOperationalModuleToggle,
  getBranchOperationalModulesError,
} from "../../services/local/branchOperationalModules";
import "../../styles/BranchOperationalModules.css";
import RestaurantContractAcceptance from "../common/RestaurantContractAcceptance";
import { getRestaurantRegistrationContract } from "../../services/supabase/restaurantContractService";
import { createContractRequestId, getContractRegistrationError } from "../../services/local/restaurantContract";
import MerchantModeSelector from "../common/MerchantModeSelector";
import { buildMerchantModeDefaults } from "../../services/local/merchantExperience";
import { WE_PEDIDOS_AVAILABLE } from "../../services/local/wePedidosAvailability";

const KIOSK_MENU_MODE_OPTIONS = [
  {
    id: "current",
    label: "Usar carta actual",
  },
  {
    id: "custom",
    label: "Crear carta propia",
  },
];

const INITIAL_MODULE_OPTIONS = [
  ["deliveryEnabled", "Delivery y retiro", "Pedidos con direccion, retiro y seguimiento."],
  ["wePedidosEnabled", "We! pedidos", "Publicacion en We! pedidos y acceso a riders."],
  ["waiterEnabled", "Equipo de mozos", "Accesos de mozo y llamados desde la mesa."],
  ["stockEnabled", "Stock e ingredientes", "Control y descuento automatico de insumos."],
  ["aiEnabled", "Asistente IA", "Asistencia y sugerencias dentro de la carta."],
];

function OnboardingRestaurante() {
  const {
    currentCompany,
    currentBranch,
    createWorkspace,
    loadingWorkspace,
    signOut,
    authError,
    user,
  } = useAuth();

  const [companyName, setCompanyName] = useState(currentCompany?.name || "");
  const [legalName, setLegalName] = useState(currentCompany?.legalName || "");
  const [companyPhone, setCompanyPhone] = useState(currentCompany?.phone || "");
  const [ownerFullName, setOwnerFullName] = useState("");
  const [ownerDocumentId, setOwnerDocumentId] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerEmail, setOwnerEmail] = useState(user?.email || "");
  const [bankAccountHolder, setBankAccountHolder] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountType, setBankAccountType] = useState("Cuenta corriente");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankCbu, setBankCbu] = useState("");
  const [bankAlias, setBankAlias] = useState("");
  const [branchName, setBranchName] = useState("Casa Central");
  const [branchPhone, setBranchPhone] = useState("");
  const [branchAddress, setBranchAddress] = useState("");
  const [branchDescription, setBranchDescription] = useState("");
  const [operationalModules, setOperationalModules] = useState(() =>
    buildMerchantModeDefaults("delivery")
  );
  const serviceMode = operationalModules.serviceMode;
  const merchantMode = serviceMode === "kiosk"
    ? "kiosk"
    : operationalModules.salonEnabled ? "restaurant" : "delivery";
  const [kioskMenuMode, setKioskMenuMode] = useState("current");
  const [kioskStationCount, setKioskStationCount] = useState(1);
  const [localError, setLocalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [contractPolicy, setContractPolicy] = useState(null);
  const [contractError, setContractError] = useState("");
  const [contractReload, setContractReload] = useState(0);
  const [contractParty, setContractParty] = useState({});
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractAuthority, setContractAuthority] = useState(false);
  const contractRequestId = useRef(null);
  const contractEnabled = contractPolicy?.enabled === true && !currentCompany?.id;

  useEffect(() => {
    let cancelled = false;
    setContractAccepted(false);
    setContractAuthority(false);
    setContractError("");
    setContractPolicy(null);
    if (currentCompany?.id) { setContractPolicy({ enabled: false }); return undefined; }
    getRestaurantRegistrationContract().then((policy) => {
      if (!cancelled) {
        setContractPolicy(policy);
        if (policy.enabled) setOwnerEmail(user?.email || "");
      }
    }).catch((error) => { if (!cancelled) setContractError(error.message); });
    return () => { cancelled = true; };
  }, [currentCompany?.id, user?.email, contractReload]);

  if (currentBranch?.id) {
    return <Navigate to="/app" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");

    if (!contractPolicy || contractError) {
      setLocalError("Espera a que se carguen las condiciones de registro o vuelve a intentarlo.");
      return;
    }
    if (contractEnabled) {
      const validation = getContractRegistrationError({ ...contractParty, legalName, ownerFullName, ownerDocumentId, branchAddress }, contractAccepted, contractAuthority);
      if (validation) { setLocalError(validation); return; }
    }

    if (!currentCompany?.id && !companyName.trim()) {
      setLocalError("El nombre de la empresa es obligatorio.");
      return;
    }

    if (!currentCompany?.id && !ownerFullName.trim()) {
      setLocalError("El nombre del titular es obligatorio.");
      return;
    }

    if (!currentCompany?.id && !ownerEmail.trim()) {
      setLocalError("El email del titular es obligatorio.");
      return;
    }

    if (!currentCompany?.id && !bankCbu.trim() && !bankAlias.trim()) {
      setLocalError("Carga al menos un CBU o alias para depositar las ganancias.");
      return;
    }

    if (!branchName.trim()) {
      setLocalError("El nombre de la primera sucursal es obligatorio.");
      return;
    }

    const moduleValidationError = getBranchOperationalModulesError({
      ...operationalModules,
      serviceMode,
    });
    if (moduleValidationError) {
      setLocalError(moduleValidationError);
      return;
    }

    setSubmitting(true);

    try {
      if (contractEnabled && !contractRequestId.current) contractRequestId.current = createContractRequestId();
      await createWorkspace({
        companyName,
        legalName,
        companyPhone,
        ownerFullName,
        ownerDocumentId,
        ownerPhone,
        ownerEmail,
        bankAccountHolder,
        bankName,
        bankAccountType,
        bankAccountNumber,
        bankCbu,
        bankAlias,
        branchName,
        branchPhone,
        branchAddress,
        branchDescription,
        ...operationalModules,
        serviceMode,
        kioskMenuMode,
        kioskStationCount,
        ...(contractEnabled ? {
          ...contractParty,
          contractAcceptance: {
            requestId: contractRequestId.current,
            versionId: contractPolicy.document.id,
            contentSha256: contractPolicy.document.contentSha256,
            accepted: contractAccepted,
            authority: contractAuthority,
          },
        } : {}),
      });
    } catch (error) {
      setLocalError(error.message || "No se pudo crear la empresa.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <div style={styles.brand}>We!Pedidos Comercios</div>
        <h1 style={styles.title}>
          {currentCompany?.id
            ? "Crea la primera sucursal"
            : "Crea tu empresa y tu primera sucursal"}
        </h1>
        <p style={styles.subtitle}>
          Tu usuario <strong>{user?.email || "actual"}</strong> ya ingreso al sistema. Ahora
          {currentCompany?.id
            ? " vamos a completar la sucursal inicial para abrir el panel."
            : " vamos a registrar la empresa y la sucursal inicial para abrir el panel."}
        </p>

        <form onSubmit={handleSubmit} style={styles.form} onChange={(event) => {
          if (!event.target.dataset.contractConsent) {
            setContractAccepted(false);
            setContractAuthority(false);
          }
        }}>
          <MerchantModeSelector
            value={merchantMode}
            onChange={(mode) => setOperationalModules(buildMerchantModeDefaults(mode))}
            disabled={submitting || loadingWorkspace}
          >
            <div style={styles.modeBlock}>
              <div style={styles.modeBlockTitle}>Herramientas y canales opcionales</div>
              <div style={styles.modeHelper}>
                El stock es opcional. Elegir Delivery no publica tu comercio en We! pedidos.
              </div>
              <div className="branch-modules__grid">
                {INITIAL_MODULE_OPTIONS.filter(([key]) =>
                  (key !== "waiterEnabled" || merchantMode === "restaurant") &&
                  (key !== "deliveryEnabled" || merchantMode !== "delivery")
                ).map(([key, title, description]) => {
                  const unavailable = key === "wePedidosEnabled" && !WE_PEDIDOS_AVAILABLE;
                  const moduleDisabled =
                    submitting ||
                    loadingWorkspace ||
                    unavailable ||
                    (key === "waiterEnabled" && !operationalModules.salonEnabled) ||
                    (key === "wePedidosEnabled" && !operationalModules.deliveryEnabled);
                  const active = !unavailable && operationalModules[key] === true;

                  return (
                    <label
                      key={key}
                      className={`branch-module-card${
                        moduleDisabled ? " branch-module-card--disabled" : ""
                      }`}
                    >
                      <span className="branch-module-card__copy">
                        <strong>{title}</strong>
                        <span>{description}</span>
                      </span>
                      <span className="branch-module-toggle">
                        <input
                          type="checkbox"
                          aria-label={title}
                          checked={active}
                          disabled={moduleDisabled}
                          onChange={(event) => {
                            const enabled = event.target.checked;
                            setOperationalModules((current) =>
                              applyBranchOperationalModuleToggle(current, key, enabled)
                            );
                          }}
                        />
                        <span className="branch-module-toggle__track" aria-hidden="true">
                          <span className="branch-module-toggle__thumb" />
                        </span>
                        <span className="branch-module-toggle__label">
                          {unavailable ? "Proximamente" : active ? "Activo" : "Pausado"}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {serviceMode === "kiosk" && (
              <div style={styles.kioskSettings}>
                <label style={styles.label}>
                  Carta del kiosk
                  <select
                    value={kioskMenuMode}
                    onChange={(event) => setKioskMenuMode(event.target.value)}
                    style={styles.input}
                    disabled={submitting || loadingWorkspace}
                  >
                    {KIOSK_MENU_MODE_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={styles.label}>
                  Cantidad de totems
                  <input
                    type="number"
                    min="1"
                    max="12"
                    step="1"
                    value={kioskStationCount}
                    onChange={(event) => setKioskStationCount(event.target.value)}
                    style={styles.input}
                    disabled={submitting || loadingWorkspace}
                  />
                </label>
              </div>
            )}
          </MerchantModeSelector>

          {!currentCompany?.id && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Empresa</h2>

              <label style={styles.label}>
                Nombre de la empresa
                <input
                  type="text"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: Grupo Mozzo"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                {contractEnabled ? "Nombre o razon social" : "Razon social (opcional)"}
                <input
                  type="text"
                  value={legalName}
                  onChange={(event) => setLegalName(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: MozzoApp S.A."
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Telefono administrativo
                <input
                  type="text"
                  value={companyPhone}
                  onChange={(event) => setCompanyPhone(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: +54 11 5555 5555"
                  disabled={submitting || loadingWorkspace}
                />
              </label>
            </div>
          )}

          {!currentCompany?.id && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Titular y cobros</h2>

              <label style={styles.label}>
                Nombre y apellido del titular
                <input
                  type="text"
                  value={ownerFullName}
                  onChange={(event) => setOwnerFullName(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: Juan Perez"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Documento
                <input
                  type="text"
                  value={ownerDocumentId}
                  onChange={(event) => setOwnerDocumentId(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: DNI 30111222"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Telefono del titular
                <input
                  type="text"
                  value={ownerPhone}
                  onChange={(event) => setOwnerPhone(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: +54 11 4444 4444"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Email del titular
                <input
                  type="email"
                  value={ownerEmail}
                  onChange={(event) => setOwnerEmail(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: titular@correo.com"
                  disabled={submitting || loadingWorkspace || contractEnabled}
                />
              </label>

              <label style={styles.label}>
                Titular de la cuenta
                <input
                  type="text"
                  value={bankAccountHolder}
                  onChange={(event) => setBankAccountHolder(event.target.value)}
                  style={styles.input}
                  placeholder="Si lo dejas vacio, usamos el nombre del titular"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Banco
                <input
                  type="text"
                  value={bankName}
                  onChange={(event) => setBankName(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: Banco Galicia"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Tipo de cuenta
                <select
                  value={bankAccountType}
                  onChange={(event) => setBankAccountType(event.target.value)}
                  style={styles.input}
                  disabled={submitting || loadingWorkspace}
                >
                  <option value="Cuenta corriente">Cuenta corriente</option>
                  <option value="Caja de ahorro">Caja de ahorro</option>
                  <option value="Cuenta sueldo">Cuenta sueldo</option>
                  <option value="Billetera virtual">Billetera virtual</option>
                </select>
              </label>

              <label style={styles.label}>
                Numero de cuenta
                <input
                  type="text"
                  value={bankAccountNumber}
                  onChange={(event) => setBankAccountNumber(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: 00123456789"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                CBU
                <input
                  type="text"
                  value={bankCbu}
                  onChange={(event) => setBankCbu(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: 0720123456789012345678"
                  disabled={submitting || loadingWorkspace}
                />
              </label>

              <label style={styles.label}>
                Alias
                <input
                  type="text"
                  value={bankAlias}
                  onChange={(event) => setBankAlias(event.target.value)}
                  style={styles.input}
                  placeholder="Ej: mi.food.cobros"
                  disabled={submitting || loadingWorkspace}
                />
              </label>
            </div>
          )}

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Primera sucursal</h2>

            <label style={styles.label}>
              Nombre de la sucursal
              <input
                type="text"
                value={branchName}
                onChange={(event) => setBranchName(event.target.value)}
                style={styles.input}
                placeholder="Ej: Casa Central"
                disabled={submitting || loadingWorkspace}
              />
            </label>

            <label style={styles.label}>
              Telefono
              <input
                type="text"
                value={branchPhone}
                onChange={(event) => setBranchPhone(event.target.value)}
                style={styles.input}
                placeholder="Ej: +54 11 5555 5555"
                disabled={submitting || loadingWorkspace}
              />
            </label>

            <label style={styles.label}>
              Domicilio
              <input
                type="text"
                value={branchAddress}
                onChange={(event) => setBranchAddress(event.target.value)}
                style={styles.input}
                placeholder="Calle, numero y ciudad"
                disabled={submitting || loadingWorkspace}
              />
            </label>

            <label style={styles.label}>
              Descripcion
              <textarea
                value={branchDescription}
                onChange={(event) => setBranchDescription(event.target.value)}
                style={{ ...styles.input, minHeight: "110px", resize: "vertical" }}
                placeholder="Cuenta brevemente que ofrece esta sucursal."
                disabled={submitting || loadingWorkspace}
              />
            </label>

          </div>

          {contractEnabled && <RestaurantContractAcceptance
            document={contractPolicy.document} party={contractParty} email={user?.email || ""}
            onPartyChange={(key, value) => setContractParty((current) => ({ ...current, [key]: value }))}
            accepted={contractAccepted} authority={contractAuthority}
            onAcceptedChange={setContractAccepted} onAuthorityChange={setContractAuthority}
            disabled={submitting || loadingWorkspace}
          />}
          {!contractPolicy && !contractError && <p role="status">Cargando condiciones de registro...</p>}
          {contractError && <div role="alert">{contractError}</div>}
          <button type="button" onClick={() => setContractReload((n) => n + 1)}
            disabled={submitting || loadingWorkspace} hidden={!contractError && !localError}>
            Volver a cargar condiciones
          </button>
          {(localError || authError) && (
            <div style={{ ...styles.banner, ...styles.bannerError }}>
              {localError || authError}
            </div>
          )}

          <div style={styles.actions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={signOut}
              disabled={submitting || loadingWorkspace}
            >
              Cerrar sesion
            </button>
            <button
              type="submit"
              style={styles.primaryButton}
              disabled={submitting || loadingWorkspace || !contractPolicy || Boolean(contractError)}
            >
              {submitting || loadingWorkspace
                ? "Creando empresa..."
                : contractEnabled ? "Aceptar contrato y crear restaurante" : "Crear y entrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "clamp(12px, 4vw, 24px)",
    backgroundColor: "#f8fafc",
  },
  panel: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    maxWidth: "720px",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "clamp(16px, 4vw, 32px)",
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.08)",
  },
  brand: {
    display: "inline-flex",
    padding: "8px 14px",
    borderRadius: "999px",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    fontWeight: 700,
    marginBottom: "16px",
  },
  title: {
    marginTop: 0,
    marginBottom: "8px",
    color: "#0f172a",
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "24px",
    color: "#475569",
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  section: {
    display: "grid",
    gap: "14px",
    padding: "18px",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    backgroundColor: "#f8fafc",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "18px",
    color: "#0f172a",
  },
  modeBlock: {
    display: "grid",
    gap: "12px",
  },
  modeBlockTitle: {
    fontWeight: 700,
    color: "#0f172a",
  },
  modeHelper: {
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  kioskSettings: {
    display: "grid",
    gap: "14px",
    padding: "16px",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#1e293b",
    fontWeight: 600,
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  banner: {
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
  },
  bannerError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  actions: {
    marginTop: "8px",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  secondaryButton: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    fontWeight: 600,
  },
  primaryButton: {
    padding: "12px 18px",
    borderRadius: "12px",
    border: "none",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
  },
};

export default OnboardingRestaurante;
